import React from 'react';
import { Link } from 'react-router-dom';
import LegalNav from '../components/LegalNav';
import SEO from '../components/SEO';
import '../styles/LegalPages.css';

const Terms = () => {
  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="legal-page-container">
      <SEO
        title="Terms of Service"
        description="Terms of service and customer conditions for Star.Ryhwa store."
        canonicalUrl="/terms"
      />
      <div className="legal-page-inner">
        {/* Breadcrumb Bar */}
        <div className="legal-breadcrumbs">
          <Link to="/">Home</Link>
          <span className="legal-breadcrumb-separator">/</span>
          <span>Trust &amp; Legal Center</span>
          <span className="legal-breadcrumb-separator">/</span>
          <span className="legal-breadcrumb-current">Terms of Service</span>
        </div>

        {/* Document Header */}
        <header className="legal-doc-header">
          <h1 className="legal-doc-title">Terms of Service</h1>
          <p className="legal-doc-description">
            Binding terms governing merchandise purchases, handmade custom orders, payment processing, shipping risk allocation, and dispute resolution.
          </p>

          <div className="legal-meta-bar">
            <div className="legal-meta-items">
              <span className="legal-meta-item">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/>
                  <line x1="8" y1="2" x2="8" y2="6"/>
                  <line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
                Last Updated: <strong>September 2026</strong>
              </span>
              <span className="legal-meta-item">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 6 12 12 16 14"/>
                </svg>
                Estimated Read: <strong>6 min read</strong>
              </span>
              <span className="legal-meta-item">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="2" y1="12" x2="22" y2="12"/>
                  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1 4-10z"/>
                </svg>
                Governing Law: <strong>State of Hawaii, USA</strong>
              </span>
              <span className="legal-meta-item">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                </svg>
                Resolution: <strong>Binding Individual Arbitration</strong>
              </span>
            </div>
            <button type="button" onClick={handlePrint} className="legal-print-btn" title="Print or Save as PDF">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="6 9 6 2 18 2 18 9"/>
                <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
                <rect x="6" y="14" width="12" height="8"/>
              </svg>
              Print / Save PDF
            </button>
          </div>
        </header>

        {/* Navigation Tab Bar */}
        <LegalNav />

        {/* Executive Summary */}
        <div className="legal-doc-summary">
          <h2>Summary of Commercial Terms</h2>
          <ul>
            <li><strong>All Sales Are Final:</strong> Due to the handcrafted nature of custom items and collectible photocard grading, returns, exchanges, and cancellations are not accepted.</li>
            <li><strong>7-Day Unboxing Video Rule (Tracked Only):</strong> Claims for damaged or incorrect items in tracked parcels require an unedited, continuous package unboxing video filed within 7 calendar days of delivery.</li>
            <li><strong>Stamped Mail Risk of Loss:</strong> Economy stamped postage carries no tracking or insurance. By choosing stamped mail, the buyer expressly assumes all risk of postal delay, damage, or loss.</li>
            <li><strong>Mandatory Informal Negotiation &amp; Arbitration:</strong> Disputes must undergo a 30-day informal negotiation before proceeding to individual binding arbitration under Hawaii law. Class actions are strictly waived.</li>
          </ul>
        </div>

        {/* Main Two-Column Layout */}
        <div className="legal-grid-layout">
          {/* Main Content Column */}
          <article className="legal-main-content">
            {/* Section 1 */}
            <section id="section-1" className="legal-doc-section">
              <h2>1. Overview &amp; Agreement to Terms</h2>
              <p>
                Welcome to Star.Ryhwa. These Terms of Service ("Terms") constitute a legally binding agreement between you ("Customer," "Buyer," or "you") and Star.Ryhwa ("we," "us," or "our") governing your access to our website, purchase of goods, and use of our services.
              </p>
              <p>
                By browsing our catalog, placing an order, or completing checkout, you expressly represent that you are at least 18 years old (or possess the verified consent and supervision of a parent or legal guardian) and that you agree to be bound by these Terms in full.
              </p>
            </section>

            {/* Section 2 */}
            <section id="section-2" className="legal-doc-section">
              <h2>2. Handmade &amp; Collectible Goods Notice</h2>
              <p>
                Star.Ryhwa specializes in bespoke handmade creations and curated collectibles (including official K-Pop album inclusions and photocards). Because our products are handcrafted or collectible, please review these industry-standard considerations:
              </p>
              <ul>
                <li><strong>Handmade Variations:</strong> Handcrafted items (such as album collages, custom hand sanitizer keychains, and phone charms) are individually assembled. Subtle variations in trimming, charm arrangement, resin curing, or placement are intentional hallmarks of artisanal work and do not constitute defects.</li>
                <li><strong>Collectible Photocard Condition:</strong> Collectible items are inspected, graded, and accurately represented. Minor factory-origin flaws—such as faint manufacturer print lines, superficial roller marks, or slight corner rounding from original album packaging—are common across the collectible industry and do not constitute damaged or defective merchandise.</li>
              </ul>
            </section>

            {/* Section 3 */}
            <section id="section-3" className="legal-doc-section">
              <h2>3. Returns, Refunds &amp; Cancellations — All Sales Are Final</h2>
              <div className="legal-doc-note">
                <p>
                  <strong>Strict All Sales Final Policy:</strong> Due to the exclusive, one-of-a-kind nature of collectible photocards and custom made-to-order artisanal crafts, <strong>all sales submitted through Star.Ryhwa are strictly final</strong>. We do not accept returns, exchanges, buyer's remorse returns, or post-submission order cancellations.
                </p>
              </div>
              <p>
                <strong>Exception for Incorrect Items (Tracked Packages Only):</strong> If an incorrect item was dispatched in a tracked package, you must submit a claim within <strong>7 calendar days of delivery</strong> to <a href="mailto:officialstar.ryhwa@gmail.com">officialstar.ryhwa@gmail.com</a> accompanied by:
              </p>
              <ul>
                <li>Your original order confirmation number.</li>
                <li>A continuous, unedited, uninterrupted video recording showing the sealed exterior shipping mailer, tracking label, and package opening process.</li>
                <li>High-resolution, focused photographs of the item received.</li>
              </ul>
              <p>
                <em>Claims submitted without an unedited unboxing video or filed outside the 7-day window cannot be honored.</em>
              </p>
            </section>

            {/* Section 4 */}
            <section id="section-4" className="legal-doc-section">
              <h2>4. Shipping Delivery &amp; Stamped Mail Risk of Loss</h2>
              <p>
                Star.Ryhwa offers both insured, tracked delivery (e.g., USPS Ground Advantage) and economy untracked stamped postage for select small items (such as single photocards and sticker packs).
              </p>
              <div className="legal-doc-note">
                <p>
                  <strong>Assumption of Risk for Untracked Stamped Mail:</strong> Stamped postage does not include tracking numbers, barcode scans, delivery confirmation, or postal transit insurance. <strong>By selecting untracked stamped postage at checkout, the buyer expressly assumes all risk of transit delay, loss, theft, and carrier mishandling.</strong> Star.Ryhwa is not liable for untracked items once handed over to the carrier.
                </p>
              </div>
              <p>
                If you require end-to-end tracking updates, delivery confirmation, and postal protection, you must select <strong>Tracked Shipping</strong> during checkout.
              </p>
            </section>

            {/* Section 5 */}
            <section id="section-5" className="legal-doc-section">
              <h2>5. International Orders, Customs Duties &amp; Taxes</h2>
              <p>
                We proudly ship to international customers worldwide. When ordering for delivery outside the United States:
              </p>
              <ul>
                <li><strong>Customer as Importer of Record:</strong> International buyers are solely responsible for any customs tariffs, value-added taxes (VAT), goods and services taxes (GST), and import clearance fees levied by your national border authorities.</li>
                <li><strong>Fees Excluded:</strong> Customs fees are strictly not included in our item prices or checkout shipping rates.</li>
                <li><strong>Refused or Unclaimed Packages:</strong> If an international shipment is delayed, confiscated, abandoned, or returned due to unpaid import taxes or customs refusal, all original shipping fees are strictly non-refundable.</li>
              </ul>
            </section>

            {/* Section 6 */}
            <section id="section-6" className="legal-doc-section">
              <h2>6. Order Acceptance &amp; Right of Cancellation</h2>
              <p>
                Receipt of an electronic order confirmation does not signify our final acceptance of your order. Star.Ryhwa reserves the right, at our sole discretion and at any time after receiving your order, to accept, decline, or limit your order for legitimate business reasons, including:
              </p>
              <ul>
                <li>Unforeseen inventory stockouts or damage to collectible materials prior to packing.</li>
                <li>Clear pricing, typographical, or technical errors on the website.</li>
                <li>Suspected fraud, unauthorized card use, or violation of these Terms.</li>
              </ul>
              <p>
                If your order is cancelled by Star.Ryhwa after payment processing, we will issue a full refund to your original payment method.
              </p>
            </section>

            {/* Section 7 */}
            <section id="section-7" className="legal-doc-section">
              <h2>7. Payment Security &amp; Hawaii General Excise Tax (GET)</h2>
              <p>
                All credit and debit card transactions are processed securely through certified, encrypted third-party payment gateways (Square, Inc., PCI-DSS Level 1). Star.Ryhwa never handles or stores your unencrypted card details.
              </p>
              <p>
                Applicable state and local taxes—including the <strong>Hawaii General Excise Tax (GET)</strong> where mandated by law—are calculated and collected during checkout based on your delivery address.
              </p>
            </section>

            {/* Section 8 */}
            <section id="section-8" className="legal-doc-section">
              <h2>8. Electronic Communications &amp; Transactions (E-SIGN Notice)</h2>
              <p>
                By visiting this site, sending emails, or completing checkout, you consent to receive communications from us electronically. You agree that all agreements, notices, disclosures, and order receipts provided electronically satisfy any legal requirement that such communications be in writing, in accordance with the Electronic Signatures in Global and National Commerce Act (E-SIGN) and Hawaii Uniform Electronic Transactions Act (UETA).
              </p>
            </section>

            {/* Section 9 */}
            <section id="section-9" className="legal-doc-section">
              <h2>9. Dispute Resolution: Informal Negotiation &amp; Binding Arbitration</h2>
              <p>
                We take immense pride in our craftsmanship and customer relationships. Before initiating any formal legal claim or payment dispute, you agree to work with us in good faith to resolve the issue:
              </p>
              <div className="legal-doc-note">
                <p>
                  <strong>Mandatory 30-Day Informal Negotiation:</strong> You agree to notify us in writing at <a href="mailto:officialstar.ryhwa@gmail.com">officialstar.ryhwa@gmail.com</a> detailing your dispute, order number, and requested remedy. Both parties agree to engage in informal, good-faith negotiations for a minimum of thirty (30) calendar days prior to initiating formal arbitration or legal proceedings.
                </p>
              </div>
              <p>
                <strong>Binding Individual Arbitration:</strong> If the dispute is not resolved through informal negotiation, it shall be resolved by binding individual arbitration administered in accordance with the commercial arbitration rules of the State of Hawaii. The seat and venue of arbitration shall be Honolulu, Hawaii (or conducted remotely by mutual consent). Judgment on the arbitration award may be entered in any court of competent jurisdiction.
              </p>
            </section>

            {/* Section 10 */}
            <section id="section-10" className="legal-doc-section">
              <h2>10. Class Action &amp; Jury Trial Waiver</h2>
              <div className="legal-doc-note">
                <p>
                  <strong>Waiver of Class and Representative Actions:</strong> YOU AND STAR.RYHWA MUTUALLY AGREE THAT ALL DISPUTES, CLAIMS, OR CONTROVERSIES MUST BE RESOLVED ON AN INDIVIDUAL BASIS. NEITHER PARTY SHALL BE ENTITLED TO JOIN, CONSOLIDATE, OR ACT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS ACTION, COLLECTIVE ACTION, PRIVATE ATTORNEY GENERAL ACTION, OR OTHER REPRESENTATIVE PROCEEDING.
                </p>
              </div>
            </section>

            {/* Section 11 */}
            <section id="section-11" className="legal-doc-section">
              <h2>11. Limitation of Liability</h2>
              <p>
                To the maximum extent permitted by applicable law, Star.Ryhwa's total cumulative liability arising out of or related to any order, product, or breach of these Terms is strictly limited to the actual amount paid by you for the specific item giving rise to the claim.
              </p>
              <p>
                In no event shall Star.Ryhwa be liable for indirect, incidental, punitive, or consequential damages, including without limitation postal delays, lost collector resale value, or postal loss on untracked mail.
              </p>
            </section>

            {/* Section 12 */}
            <section id="section-12" className="legal-doc-section">
              <h2>12. Governing Law &amp; Jurisdiction</h2>
              <p>
                These Terms and all transactions conducted on this website are governed by and construed in accordance with the laws of the <strong>State of Hawaii, United States of America</strong>, without regard to its conflict of law principles.
              </p>
            </section>

            {/* Section 13 */}
            <section id="section-13" className="legal-doc-section">
              <h2>13. Modifications to Terms</h2>
              <p>
                We reserve the right to revise or update these Terms at any time. Changes become effective immediately upon posting. Your continued use of the website or placement of orders following the posting of updated Terms constitutes your binding acceptance.
              </p>
            </section>

            {/* Section 14 */}
            <section id="section-14" className="legal-doc-section">
              <h2>14. Store Inquiries &amp; Customer Support</h2>
              <p>
                Have questions regarding store terms, custom orders, or ordering policies? Please contact our studio desk:
              </p>
              <ul className="legal-contact-list">
                <li><strong>Email:</strong> <a href="mailto:officialstar.ryhwa@gmail.com">officialstar.ryhwa@gmail.com</a></li>
                <li><strong>Instagram:</strong> <a href="https://www.instagram.com/star.ryhwa" target="_blank" rel="noopener noreferrer">@star.ryhwa</a></li>
                <li><strong>Studio Location:</strong> State of Hawaii, United States</li>
              </ul>
            </section>
          </article>

          {/* Sticky Table of Contents Sidebar */}
          <aside className="legal-sidebar">
            <div className="legal-toc-container">
              <div className="legal-toc-title">On This Page</div>
              <ul className="legal-toc-list">
                <li><a href="#section-1" className="legal-toc-link">1. Agreement to Terms</a></li>
                <li><a href="#section-2" className="legal-toc-link">2. Handmade &amp; Collectibles</a></li>
                <li><a href="#section-3" className="legal-toc-link">3. All Sales Final &amp; Claims</a></li>
                <li><a href="#section-4" className="legal-toc-link">4. Stamped Shipping Risk</a></li>
                <li><a href="#section-5" className="legal-toc-link">5. Customs &amp; International</a></li>
                <li><a href="#section-6" className="legal-toc-link">6. Order Acceptance Rights</a></li>
                <li><a href="#section-7" className="legal-toc-link">7. Payment &amp; Hawaii GET</a></li>
                <li><a href="#section-8" className="legal-toc-link">8. E-SIGN Electronic Consent</a></li>
                <li><a href="#section-9" className="legal-toc-link">9. Dispute Resolution &amp; Arbitration</a></li>
                <li><a href="#section-10" className="legal-toc-link">10. Class Action Waiver</a></li>
                <li><a href="#section-11" className="legal-toc-link">11. Limitation of Liability</a></li>
                <li><a href="#section-12" className="legal-toc-link">12. Governing Law</a></li>
                <li><a href="#section-13" className="legal-toc-link">13. Modifications</a></li>
                <li><a href="#section-14" className="legal-toc-link">14. Inquiries &amp; Support</a></li>
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default Terms;
